// Code generated by @hex-insights/frontend-generator; While this comment is present this file may be overwritten. If manual edits are made, remove this comment.
import * as Apollo from "@apollo/client";
import { getListQueryStatus, IndexForms } from "@hex-insights/app-modules";
import {
	ClassNameProps,
	Conditional,
	Else,
	filterObject,
	getObjectKeys,
	If,
	makeClassName,
	mappedArray,
	Row,
	StyleProps,
	TypeOrArray,
} from "@hex-insights/core";
import { FormState } from "@hex-insights/forms";
import { Table } from "@hex-insights/tables";
import { EnrollmentApplicationGuardianFilterInput, EnrollmentApplicationGuardianOrderField } from "../../../Utilities";
import { EnrollmentApplicationGuardianFormat } from "../../Format";
import { EnrollmentApplicationGuardianLink } from "../../Links";
import { EnrollmentApplicationGuardianPaginationForm } from "../../Pagination";
import { ExportButton } from "./Export";
import { DefaultNode, GenericEnrollmentApplicationGuardianIndexQuery } from "./types";

const defaultColumns = {
	name: true,
	givenName: true,
	middleName: true,
	familyName: true,
	nameOrder: true,
	dateOfBirth: true,
	gender: true,
	relationshipType: true,
	relationshipStatus: true,
	emailAddress: true,
	phoneNumber: true,
	addressLine1: true,
	addressLine2: true,
	addressCity: true,
	addressPostalCode: true,
	addressStateOrProvince: true,
	isPrimary: true,
	isFinancialContact: true,
	isEmergencyContact: true,
	isAuthorizedForPickup: true,
	livesWithStudent: true,
	isPrimaryOnApplication: true,
};

export const columnNames = getObjectKeys(defaultColumns);

export type IndexTableProps = {
	paginationFormState: FormState<IndexForms.PaginationFormValues>;
	queryVariables: Pick<
		IndexForms.QueryVariables<
			TypeOrArray<EnrollmentApplicationGuardianFilterInput>,
			EnrollmentApplicationGuardianOrderField | null
		>,
		"filters" | "order" | "pagination"
	>;
	loading: boolean;
	data?: GenericEnrollmentApplicationGuardianIndexQuery<DefaultNode>;
	error?: Apollo.ApolloError;
	paginationInfo: IndexForms.PaginationInfo;
	setPaginationInfo: React.Dispatch<React.SetStateAction<IndexForms.PaginationInfo>>;
	scrollSyncID: number;
	synchronizeScroll: () => void;
	tableSortingProps: IndexForms.TableSortingProps<string>;
	columns?: Record<string, boolean>;
	numFillerRows: number;
	noFillerRows?: boolean;
	noHeader?: boolean;
	noToolbar?: boolean;
	noExport?: boolean;
} & Partial<ClassNameProps & StyleProps>;

export function IndexTable({
	paginationFormState,
	queryVariables,
	loading,
	data,
	error,
	paginationInfo,
	setPaginationInfo,
	scrollSyncID,
	synchronizeScroll,
	tableSortingProps,
	columns = defaultColumns,
	numFillerRows,
	noFillerRows = false,
	noHeader = false,
	noToolbar = false,
	noExport = false,
	className,
	style,
}: IndexTableProps) {
	const status = getListQueryStatus({ loading, data, error, resultKey: "enrollmentApplicationGuardianConnection" });

	const columnNames = getObjectKeys(filterObject(columns, (_, v) => v));

	return (
		<Table.Container className={makeClassName("hub__index-page__table__container", className)} style={style}>
			<Table
				columns={columnNames}
				minColumnWidth="10rem"
				scrollSyncID={scrollSyncID}
				{...tableSortingProps}
				className="hub__index-page__table"
			>
				<If condition={!noHeader}>
					<Table.Header className="hub__index-page__table__header">
						<Table.Row>
							<If condition={columns.name}>
								<Table.Heading column="name">Name</Table.Heading>
							</If>
							<If condition={columns.givenName}>
								<Table.Heading column="givenName" sortable>
									Given Name
								</Table.Heading>
							</If>
							<If condition={columns.middleName}>
								<Table.Heading column="middleName">Middle Name</Table.Heading>
							</If>
							<If condition={columns.familyName}>
								<Table.Heading column="familyName" sortable>
									Family Name
								</Table.Heading>
							</If>
							<If condition={columns.nameOrder}>
								<Table.Heading column="nameOrder">Name Order</Table.Heading>
							</If>
							<If condition={columns.dateOfBirth}>
								<Table.Heading column="dateOfBirth" sortable>
									Date of Birth
								</Table.Heading>
							</If>
							<If condition={columns.gender}>
								<Table.Heading column="gender">Gender</Table.Heading>
							</If>
							<If condition={columns.relationshipType}>
								<Table.Heading column="relationshipType" sortable>
									Relationship Type
								</Table.Heading>
							</If>
							<If condition={columns.relationshipStatus}>
								<Table.Heading column="relationshipStatus">Relationship Status</Table.Heading>
							</If>
							<If condition={columns.emailAddress}>
								<Table.Heading column="emailAddress">Email Address</Table.Heading>
							</If>
							<If condition={columns.phoneNumber}>
								<Table.Heading column="phoneNumber">Phone Number</Table.Heading>
							</If>
							<If condition={columns.addressLine1}>
								<Table.Heading column="addressLine1">Address Line 1</Table.Heading>
							</If>
							<If condition={columns.addressLine2}>
								<Table.Heading column="addressLine2">Address Line 2</Table.Heading>
							</If>
							<If condition={columns.addressCity}>
								<Table.Heading column="addressCity">Address City</Table.Heading>
							</If>
							<If condition={columns.addressPostalCode}>
								<Table.Heading column="addressPostalCode">Address Postal Code</Table.Heading>
							</If>
							<If condition={columns.addressStateOrProvince}>
								<Table.Heading column="addressStateOrProvince">Address State or Province</Table.Heading>
							</If>
							<If condition={columns.isPrimary}>
								<Table.Heading column="isPrimary" sortable>
									Is Primary
								</Table.Heading>
							</If>
							<If condition={columns.isFinancialContact}>
								<Table.Heading column="isFinancialContact" sortable>
									Is Financial Contact
								</Table.Heading>
							</If>
							<If condition={columns.isEmergencyContact}>
								<Table.Heading column="isEmergencyContact" sortable>
									Is Emergency Contact
								</Table.Heading>
							</If>
							<If condition={columns.isAuthorizedForPickup}>
								<Table.Heading column="isAuthorizedForPickup" sortable>
									Is Authorized for Pickup
								</Table.Heading>
							</If>
							<If condition={columns.livesWithStudent}>
								<Table.Heading column="livesWithStudent" sortable>
									Lives with Student
								</Table.Heading>
							</If>
							<If condition={columns.isPrimaryOnApplication}>
								<Table.Heading column="isPrimaryOnApplication">Is Primary on Application</Table.Heading>
							</If>
						</Table.Row>
					</Table.Header>
				</If>

				<Table.Body className="hub__index-page__table__body">
					<Conditional>
						<If condition={status === "results"}>
							{data?.enrollmentApplicationGuardianConnection.edges.map(({ node }) => (
								<Table.Row key={node.id} className="hub__index-page__table__row">
									<If condition={columns.name}>
										<Table.Cell column="name" oneLine>
											<EnrollmentApplicationGuardianLink instance={node}>
												{EnrollmentApplicationGuardianFormat.Fields.name(node.name)}
											</EnrollmentApplicationGuardianLink>
										</Table.Cell>
									</If>
									<If condition={columns.givenName}>
										<Table.Cell column="givenName" oneLine>
											{EnrollmentApplicationGuardianFormat.Fields.givenName(node.givenName)}
										</Table.Cell>
									</If>
									<If condition={columns.middleName}>
										<Table.Cell column="middleName" oneLine>
											{EnrollmentApplicationGuardianFormat.Fields.middleName(node.middleName)}
										</Table.Cell>
									</If>
									<If condition={columns.familyName}>
										<Table.Cell column="familyName" oneLine>
											{EnrollmentApplicationGuardianFormat.Fields.familyName(node.familyName)}
										</Table.Cell>
									</If>
									<If condition={columns.nameOrder}>
										<Table.Cell column="nameOrder" oneLine>
											{EnrollmentApplicationGuardianFormat.Fields.nameOrder(node.nameOrder)}
										</Table.Cell>
									</If>
									<If condition={columns.dateOfBirth}>
										<Table.Cell column="dateOfBirth" oneLine>
											{EnrollmentApplicationGuardianFormat.Fields.dateOfBirth(node.dateOfBirth)}
										</Table.Cell>
									</If>
									<If condition={columns.gender}>
										<Table.Cell column="gender" oneLine>
											{EnrollmentApplicationGuardianFormat.Fields.gender(node.gender)}
										</Table.Cell>
									</If>
									<If condition={columns.relationshipType}>
										<Table.Cell column="relationshipType" oneLine>
											{EnrollmentApplicationGuardianFormat.Fields.relationshipType(node.relationshipType)}
										</Table.Cell>
									</If>
									<If condition={columns.relationshipStatus}>
										<Table.Cell column="relationshipStatus" oneLine>
											{EnrollmentApplicationGuardianFormat.Fields.relationshipStatus(node.relationshipStatus)}
										</Table.Cell>
									</If>
									<If condition={columns.emailAddress}>
										<Table.Cell column="emailAddress" oneLine>
											{EnrollmentApplicationGuardianFormat.Fields.emailAddress(node.emailAddress)}
										</Table.Cell>
									</If>
									<If condition={columns.phoneNumber}>
										<Table.Cell column="phoneNumber" oneLine>
											{EnrollmentApplicationGuardianFormat.Fields.phoneNumber(node.phoneNumber)}
										</Table.Cell>
									</If>
									<If condition={columns.addressLine1}>
										<Table.Cell column="addressLine1" oneLine>
											{EnrollmentApplicationGuardianFormat.Fields.addressLine1(node.addressLine1)}
										</Table.Cell>
									</If>
									<If condition={columns.addressLine2}>
										<Table.Cell column="addressLine2" oneLine>
											{EnrollmentApplicationGuardianFormat.Fields.addressLine2(node.addressLine2)}
										</Table.Cell>
									</If>
									<If condition={columns.addressCity}>
										<Table.Cell column="addressCity" oneLine>
											{EnrollmentApplicationGuardianFormat.Fields.addressCity(node.addressCity)}
										</Table.Cell>
									</If>
									<If condition={columns.addressPostalCode}>
										<Table.Cell column="addressPostalCode" oneLine>
											{EnrollmentApplicationGuardianFormat.Fields.addressPostalCode(node.addressPostalCode)}
										</Table.Cell>
									</If>
									<If condition={columns.addressStateOrProvince}>
										<Table.Cell column="addressStateOrProvince" oneLine>
											{EnrollmentApplicationGuardianFormat.Fields.addressStateOrProvince(node.addressStateOrProvince)}
										</Table.Cell>
									</If>
									<If condition={columns.isPrimary}>
										<Table.Cell column="isPrimary" oneLine>
											{EnrollmentApplicationGuardianFormat.Fields.isPrimary(node.isPrimary)}
										</Table.Cell>
									</If>
									<If condition={columns.isFinancialContact}>
										<Table.Cell column="isFinancialContact" oneLine>
											{EnrollmentApplicationGuardianFormat.Fields.isFinancialContact(node.isFinancialContact)}
										</Table.Cell>
									</If>
									<If condition={columns.isEmergencyContact}>
										<Table.Cell column="isEmergencyContact" oneLine>
											{EnrollmentApplicationGuardianFormat.Fields.isEmergencyContact(node.isEmergencyContact)}
										</Table.Cell>
									</If>
									<If condition={columns.isAuthorizedForPickup}>
										<Table.Cell column="isAuthorizedForPickup" oneLine>
											{EnrollmentApplicationGuardianFormat.Fields.isAuthorizedForPickup(node.isAuthorizedForPickup)}
										</Table.Cell>
									</If>
									<If condition={columns.livesWithStudent}>
										<Table.Cell column="livesWithStudent" oneLine>
											{EnrollmentApplicationGuardianFormat.Fields.livesWithStudent(node.livesWithStudent)}
										</Table.Cell>
									</If>
									<If condition={columns.isPrimaryOnApplication}>
										<Table.Cell column="isPrimaryOnApplication" oneLine>
											{EnrollmentApplicationGuardianFormat.Fields.isPrimaryOnApplication(node.isPrimaryOnApplication)}
										</Table.Cell>
									</If>
								</Table.Row>
							))}
							<If condition={!noFillerRows}>
								{mappedArray(numFillerRows, (i) => (
									<Table.FillerRow
										key={i}
										className="hub__index-page__table__row hub__index-page__table__row--filler"
									/>
								))}
							</If>
						</If>
						<Else>
							<Table.Row className="hub__index-page__table__row">
								<Table.Cell column={columnNames[0]} colspan={columnNames.length}>
									<Conditional>
										<If condition={status === "no-results"}>No results found.</If>
										<If condition={status === "error"}>Something went wrong</If>
										<If condition={status === "loading"}>Loading...</If>
									</Conditional>
								</Table.Cell>
							</Table.Row>
							<If condition={!noFillerRows}>
								{mappedArray((paginationFormState.formValues.resultsPerPage ?? 1) - 1, (i) => (
									<Table.FillerRow
										key={i}
										className="hub__index-page__table__row hub__index-page__table__row--filler"
									/>
								))}
							</If>
						</Else>
					</Conditional>
				</Table.Body>
			</Table>

			<If condition={!noToolbar}>
				<Table.Toolbar>
					<Row justify="space-between" align="center">
						<EnrollmentApplicationGuardianPaginationForm.PaginationForm
							formState={paginationFormState}
							filters={queryVariables.filters}
							order={queryVariables.order}
							setPaginationInfo={setPaginationInfo}
							onNavigate={synchronizeScroll}
						/>

						<Row justify="spaced-start" align="center">
							<If condition={!noExport}>
								<ExportButton
									queryVariables={queryVariables}
									resultsPerPage={paginationFormState.formValues.resultsPerPage}
									columns={columns}
								/>
							</If>

							<span>
								<Conditional>
									<If condition={status === "results"}>{IndexForms.getPaginationDescription(paginationInfo)}</If>
									<If condition={status === "no-results"}>No results found.</If>
									<If condition={status === "error"}>Something went wrong.</If>
									<If condition={status === "loading"}>Loading...</If>
								</Conditional>
							</span>
						</Row>
					</Row>
				</Table.Toolbar>
			</If>
		</Table.Container>
	);
}
